import {
  useCallback,
  useMemo,
  useState,
  createContext,
  useContext,
  PropsWithChildren,
} from 'react'

import { useMedia } from '@/hooks/useMedia'

interface MenuContextValue {
  handleMenuMouseLeave: () => void
  handleMenuMouseOver: () => void
  isMenuOpen: boolean
  isMobile: boolean
  toggleIsMenuOpen: () => void
}

const MenuContext = createContext<MenuContextValue | undefined>(undefined)

export const MenuProvider = ({ children }: PropsWithChildren) => {
  const isMobile = useMedia(['(max-width: 768px)'], [true], false)
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  const toggleIsMenuOpen = useCallback(
    () => setIsMenuOpen(!isMenuOpen),
    [isMenuOpen]
  )

  const handleMenuMouseLeave = useCallback(() => {
    if (!isMobile && isMenuOpen) {
      setIsMenuOpen(false)
    }
  }, [isMenuOpen, isMobile])

  const handleMenuMouseOver = useCallback(() => {
    if (!isMobile && !isMenuOpen) {
      setIsMenuOpen(true)
    }
  }, [isMenuOpen, isMobile])

  const value = useMemo(
    () => ({
      handleMenuMouseLeave,
      handleMenuMouseOver,
      isMenuOpen,
      isMobile,
      toggleIsMenuOpen,
    }),
    [
      handleMenuMouseLeave,
      handleMenuMouseOver,
      isMenuOpen,
      isMobile,
      toggleIsMenuOpen,
    ]
  )

  return <MenuContext.Provider value={value}>{children}</MenuContext.Provider>
}

export const useMenuContext = () => {
  const context = useContext(MenuContext)
  if (context === undefined) {
    throw new Error('useMenuContext must be used within a MenuProvider')
  }
  return context
}
